import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AppButton } from "../ui";
import "./StudentsToWatch.scss";

/**
 * StudentsToWatch
 * 
 * Highlights students who may need intervention based on:
 * - Low scores (<40%)
 * - Blank/no responses
 * - Very short session times
 * 
 * Decision it enables: "Which students should I check in with?"
 */
export default function StudentsToWatch({ 
  sessions = [], 
  limit = 6,
  scoreThreshold = 40,
  showViewAll = true
}) {
  const navigate = useNavigate();

  const studentsNeedingAttention = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    return sessions
      .map(session => {
        const score = session.meanScore ?? null;
        const responseCount = session.responseCount || 0;
        const timeSpent = session.secondsSpent || 0;
        const sessionCode = session.sessionCode || session.code || "Unknown";

        // Calculate concern level
        let concernLevel = "ok";
        let concernReasons = [];

        // Check for issues
        if (score !== null && score < scoreThreshold) {
          concernLevel = score < 20 ? "critical" : "warning";
          concernReasons.push(`${Math.round(score)}% avg`);
        }

        if (responseCount === 0) {
          concernLevel = "critical";
          concernReasons.push("No responses");
        } else if (responseCount === 1 && score !== null && score < scoreThreshold) {
          concernReasons.push("Only 1 response");
        }

        if (timeSpent < 30 && responseCount > 0) {
          if (concernLevel !== "critical") concernLevel = "warning";
          concernReasons.push("Very quick session");
        }

        return {
          sessionCode,
          score,
          responseCount,
          timeSpent,
          concernLevel,
          concernReasons,
          startTime: session.startTime || session.timestamp,
        };
      })
      .filter(s => s.concernLevel !== "ok")
      .sort((a, b) => {
        // Critical first, then warning
        if (a.concernLevel === "critical" && b.concernLevel !== "critical") return -1;
        if (b.concernLevel === "critical" && a.concernLevel !== "critical") return 1;
        // Then by score (lowest first)
        return (a.score ?? 0) - (b.score ?? 0);
      })
      .slice(0, limit);
  }, [sessions, limit, scoreThreshold]);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStudentClick = (sessionCode) => {
    navigate(`/sessions/${sessionCode}`);
  };

  if (studentsNeedingAttention.length === 0) {
    return (
      <div className="studentsWatch">
        <div className="swEmpty">
          <div className="swEmptyIcon">✓</div>
          <div className="swEmptyText">All students on track</div>
          <div className="swEmptySub">No students currently flagged for attention</div>
        </div>
      </div>
    );
  }

  const criticalCount = studentsNeedingAttention.filter(s => s.concernLevel === "critical").length;
  const warningCount = studentsNeedingAttention.filter(s => s.concernLevel === "warning").length;

  return (
    <div className="studentsWatch">
      {/* Summary */}
      <div className="swSummary">
        {criticalCount > 0 && (
          <span className="swSummaryBadge swSummaryBadge--critical">
            {criticalCount} need{criticalCount === 1 ? 's' : ''} help
          </span>
        )}
        {warningCount > 0 && (
          <span className="swSummaryBadge swSummaryBadge--warning">
            {warningCount} to check
          </span>
        )}
      </div>

      {/* Student list */}
      <div className="swList">
        {studentsNeedingAttention.map((student, idx) => (
          <div 
            key={student.sessionCode || idx}
            className={`swItem swItem--${student.concernLevel}`}
            onClick={() => handleStudentClick(student.sessionCode)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleStudentClick(student.sessionCode)}
          >
            {/* Status indicator */}
            <div className={`swStatus swStatus--${student.concernLevel}`}>
              {student.concernLevel === "critical" ? "!" : "?"}
            </div>

            {/* Student info */}
            <div className="swInfo">
              <div className="swName">
                <span className="swNameLabel">Anonymous</span>
                <span className="swNameCode">{student.sessionCode}</span>
              </div>
              <div className="swDetails">
                {student.concernReasons.map((reason, i) => (
                  <span key={i} className="swReason">
                    {reason}
                    {i < student.concernReasons.length - 1 && <span className="swReasonDot">·</span>}
                  </span>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div className="swMetrics">
              <div className="swMetric">
                <span className="swMetricValue">
                  {student.score !== null ? `${Math.round(student.score)}%` : "—"}
                </span>
                <span className="swMetricLabel">Score</span>
              </div>
              <div className="swMetric">
                <span className="swMetricValue">{student.responseCount}</span>
                <span className="swMetricLabel">Insights</span>
              </div>
              <div className="swMetric">
                <span className="swMetricValue">{formatTime(student.timeSpent)}</span>
                <span className="swMetricLabel">Time</span>
              </div>
            </div>

            {/* Action */}
            <AppButton className="swAction" kind="secondary" type="button">
              View
            </AppButton>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="swFooter">
        <span className="swFooterText">
          Students with scores below {scoreThreshold}% or incomplete sessions
        </span>
        {showViewAll && sessions.length > limit && (
          <AppButton
            className="swViewAll"
            kind="ghost"
            type="button"
            onClick={() => navigate('/sessions')}
          >
            View All →
          </AppButton>
        )}
      </div>
    </div>
  );
}
